import React, { cloneElement, ReactElement } from 'react';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { Tooltip } from '../feedback/Tooltip';
import { IconHelp } from '../icon/IconHelp';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { BusinessStatsUI } from './types';

interface StatSectionSkeletonProps {
    statsSlot: ReactElement;
}

const StatSectionSkeleton = (props: StatSectionSkeletonProps) => {
    const { statsSlot } = props;

    return (
        <Flex
            background="var(--background-primary)"
            width="100%"
            pt={2}
            px={2}
            borderRadius={2}
            spacing={1.5}
        >
            <Skeleton width="30%" variant="text" height={24} />
            <Flex width="100%" direction="row" flexWrap="wrap">
                {cloneElement(statsSlot)}
            </Flex>
        </Flex>
    );
};

const StatItemSkeleton = () => (
    <Flex width="calc(100% / 3)" spacing={0.5} pb={2}>
        <Skeleton width="80px" variant="text" height={14} />
        <Skeleton width="80px" variant="text" height={24} />
    </Flex>
);

export const BusinessStatisticSkeleton = () => (
    <Flex width="100%" spacing={2}>
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
        <StatSectionSkeleton
            statsSlot={
                <>
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                    <StatItemSkeleton />
                </>
            }
        />
    </Flex>
);

interface StatItemProps {
    title: string;
    displayValue: string;
    diffValue: number;
    diffDisplay: string;
}

const StatItem = (props: StatItemProps) => {
    const { title, displayValue, diffValue, diffDisplay } = props;

    return (
        <Flex width="calc(100% / 3)" spacing={0.5} pb={2}>
            <Typography variant="caption" color="secondary">
                {title}
            </Typography>
            <Flex direction="row" spacing={1} alignItems="end">
                <Typography variant="title3" weight="bold">
                    {displayValue}
                </Typography>
                {diffValue ? (
                    <Typography
                        variant="caption"
                        color={diffValue >= 0 ? 'var(--color-success)' : 'var(--color-danger)'}
                    >
                        {diffDisplay}
                    </Typography>
                ) : (
                    ''
                )}
            </Flex>
        </Flex>
    );
};

interface StatSectionProps {
    title: string;
    statsSlot: ReactElement;
    helpMessage: ReactElement;
}

const StatSection = (props: StatSectionProps) => {
    const { title, statsSlot, helpMessage } = props;

    return (
        <Flex
            background="var(--background-primary)"
            width="100%"
            pt={2}
            px={2}
            borderRadius={2}
            spacing={1.5}
        >
            <Flex direction="row" spacing={0.5} alignItems="center">
                <Typography variant="title3" weight="bold">
                    {title}
                </Typography>
                <Tooltip
                    trigger={<IconHelp color="var(--color-primary)" cursor="help" size={16} />}
                >
                    {cloneElement(helpMessage)}
                </Tooltip>
            </Flex>
            <Flex width="100%" direction="row" flexWrap="wrap">
                {cloneElement(statsSlot)}
            </Flex>
        </Flex>
    );
};

interface BusinessStatisticSummaryUIProps {
    statistic: BusinessStatsUI;
}

export const BusinessStatisticSummaryUI = (props: BusinessStatisticSummaryUIProps) => {
    const t = useTranslation();
    const { statistic } = props;
    const { coverage, involved, shows, subscribers, interactions } = statistic;

    return (
        <Flex width="100%" spacing={2}>
            {coverage && (
                <StatSection
                    title={t('Охват')}
                    helpMessage={
                        <>
                            <Typography variant="caption" weight="bold" color="var(--color-invert)">
                                {t('Охват')}
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t(
                                        ' - количество уникальных аккаунтов, владельцы которых хотя бы 1 раз видели ваш контент за выбранный период с изменением в процентах относительного предыдущего периода.',
                                    )}
                                </Typography>
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Эта метрика находится в разработке и возможны изменения в расчетах.',
                                )}
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Охват считается отдельно для пользователей- подписчиков и пользователей, которые не подписаны на профиль.',
                                )}
                            </Typography>
                        </>
                    }
                    statsSlot={
                        <>
                            <StatItem
                                title={t('Всего')}
                                displayValue={coverage.total.display}
                                diffValue={coverage.total.diffValue}
                                diffDisplay={coverage.total.diffDisplay}
                            />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={coverage.subscribers.display}
                                diffValue={coverage.subscribers.diffValue}
                                diffDisplay={coverage.subscribers.diffDisplay}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={coverage.notsubscribers.display}
                                diffValue={coverage.notsubscribers.diffValue}
                                diffDisplay={coverage.notsubscribers.diffDisplay}
                            />
                        </>
                    }
                />
            )}

            {involved && (
                <StatSection
                    title={t('Вовлеченные аккаунты')}
                    helpMessage={
                        <>
                            <Typography variant="caption" weight="bold" color="var(--color-invert)">
                                {t('Вовлеченные аккаунты')}
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t(
                                        ' - количество уникальных аккаунтов, владельцы которых совершили какое-либо полезное действие с контентом профиля за выбранный период с изменением в процентах относительного предыдущего периода. Полезные действия - подписка, лайк, сохранение в избранное, поделиться контентом.',
                                    )}
                                </Typography>
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Эта метрика находится в разработке и возможны изменения в расчетах.',
                                )}
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Вовлеченные аккаунты считаются отдельно для пользователей - подписчиков и пользователей, которые не подписаны на профиль.',
                                )}
                            </Typography>
                        </>
                    }
                    statsSlot={
                        <>
                            <StatItem
                                title={t('Всего')}
                                displayValue={involved.total.display}
                                diffValue={involved.total.diffValue}
                                diffDisplay={involved.total.diffDisplay}
                            />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={involved.subscribers.display}
                                diffValue={involved.subscribers.diffValue}
                                diffDisplay={involved.subscribers.diffDisplay}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={involved.notsubscribers.display}
                                diffValue={involved.notsubscribers.diffValue}
                                diffDisplay={involved.notsubscribers.diffDisplay}
                            />
                        </>
                    }
                />
            )}

            {shows && (
                <StatSection
                    title={t('Показы')}
                    helpMessage={
                        <>
                            <Typography variant="caption" weight="bold" color="var(--color-invert)">
                                {t('Показы')}
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t(
                                        ' - число показов публикаций за выбранный период времени с изменением относительно предыдущего периода времени. Показы считаются с разбивкой по подписанным и неподписанным аккаунтам.',
                                    )}
                                </Typography>
                            </Typography>
                        </>
                    }
                    statsSlot={
                        <>
                            <StatItem
                                title={t('Всего')}
                                displayValue={shows.total.display}
                                diffValue={shows.total.diffValue}
                                diffDisplay={shows.total.diffDisplay}
                            />
                            <StatItem
                                title={t('Подписчики')}
                                displayValue={shows.subscribers.display}
                                diffValue={shows.subscribers.diffValue}
                                diffDisplay={shows.subscribers.diffDisplay}
                            />
                            <StatItem
                                title={t('Не подписчики')}
                                displayValue={shows.notsubscribers.display}
                                diffValue={shows.notsubscribers.diffValue}
                                diffDisplay={shows.notsubscribers.diffDisplay}
                            />
                        </>
                    }
                />
            )}

            {subscribers && (
                <StatSection
                    title={t('Подписчики')}
                    helpMessage={
                        <>
                            <Typography variant="caption" weight="bold" color="var(--color-invert)">
                                {t('Подписчики')}
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t(' - количество аккаунтов, подписавшихся на профиль за ')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t('весь срок существования профиля')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t(' с изменением в количестве подписавшихся за ')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    weight="bold"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t('выбранный период')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t('.')}
                                </Typography>
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Подписались - количество аккаунтов, подписавшихся за выбранный период.',
                                )}
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Отписались - количество аккаунтов, отписавшихся от профиля за выбранный период.',
                                )}
                            </Typography>
                        </>
                    }
                    statsSlot={
                        <>
                            <StatItem
                                title={t('Всего')}
                                displayValue={subscribers.total.display}
                                diffValue={subscribers.total.diffValue}
                                diffDisplay={subscribers.total.diffDisplay}
                            />
                            <StatItem
                                title={t('Подписались')}
                                displayValue={subscribers.subscribed.display}
                                diffValue={subscribers.subscribed.diffValue}
                                diffDisplay={subscribers.subscribed.diffDisplay}
                            />
                            <StatItem
                                title={t('Отписались')}
                                displayValue={subscribers.unsubscribed.display}
                                diffValue={subscribers.unsubscribed.diffValue}
                                diffDisplay={subscribers.unsubscribed.diffDisplay}
                            />
                        </>
                    }
                />
            )}

            {interactions && (
                <StatSection
                    title={t('Взаимодействия')}
                    helpMessage={
                        <>
                            <Typography variant="caption" weight="bold" color="var(--color-invert)">
                                {t('Взаимодействия')}
                                <Typography
                                    variant="caption"
                                    color="var(--color-invert)"
                                    display="inline"
                                >
                                    {t(
                                        ' - количество действий, которые совершали пользователи с контентом за выбранный период с изменением относительно предыдущего периода.',
                                    )}
                                </Typography>
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t(
                                    'Клики по ссылкам - количество переходов по ссылкам из поста и профиля.',
                                )}
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t('Лайки - количество лайков у контента.')}
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t('Шаринги - количество шарингов контента.')}
                            </Typography>
                            <Typography variant="caption" color="var(--color-invert)">
                                {t('Сохранения - количество сохранненого контента в избранное.')}
                            </Typography>
                        </>
                    }
                    statsSlot={
                        <>
                            <StatItem
                                title={t('Всего')}
                                displayValue={interactions.total.display}
                                diffValue={interactions.total.diffValue}
                                diffDisplay={interactions.total.diffDisplay}
                            />
                            <StatItem
                                title={t('Клики по ссылке')}
                                displayValue={interactions.clicks.display}
                                diffValue={interactions.clicks.diffValue}
                                diffDisplay={interactions.clicks.diffDisplay}
                            />
                            <StatItem
                                title={t('Лайки')}
                                displayValue={interactions.likes.display}
                                diffValue={interactions.likes.diffValue}
                                diffDisplay={interactions.likes.diffDisplay}
                            />
                            <StatItem
                                title={t('Шаринги')}
                                displayValue={interactions.shares.display}
                                diffValue={interactions.shares.diffValue}
                                diffDisplay={interactions.shares.diffDisplay}
                            />
                            <StatItem
                                title={t('Сохранения')}
                                displayValue={interactions.saves.display}
                                diffValue={interactions.saves.diffValue}
                                diffDisplay={interactions.saves.diffDisplay}
                            />
                        </>
                    }
                />
            )}
        </Flex>
    );
};
